'use strict';

import { matchesBreakpoint, queryFirst, queryAll, addClass, removeClass, hasClass } from '../domUtil';
import { HIDDEN_CLASS, PLAYED_CLASS, MUTED_CLASS, UNMUTED_CLASS } from '../constants';
import { loadLazyVideo } from '../imageLazyLoad';

const USER_PAUSED_ATTR = 'data-user-paused';
const mainVideoClass = 'video-component-main';
const mobileVideoClass = 'video-component-mobile';
/** Seconds of playback before trusting Chromium's decoded-audio byte count. */
const AUDIO_DETECT_PLAYBACK_SECONDS = 0.25;
let highlightShopProxyInitialized = false;

/**
 * Detect whether a video has an audio track.
 * Prefer an explicit data-has-audio flag when present; otherwise use browser APIs.
 * Chromium's webkitAudioDecodedByteCount is only trusted after some playback.
 *
 * @param {HTMLVideoElement} video
 * @returns {{ hasAudio: boolean, confident: boolean }}
 */
function detectVideoAudio(video) {
    if (!video) return { hasAudio: true, confident: false };

    const componentFlag = video.closest?.('.video-component')?.dataset?.hasAudio;
    const flag = video.dataset.hasAudio || componentFlag;
    if (flag === 'false') return { hasAudio: false, confident: true };
    if (flag === 'true') return { hasAudio: true, confident: true };

    if (typeof video.mozHasAudio === 'boolean') {
        return { hasAudio: video.mozHasAudio, confident: true };
    }

    if (video.audioTracks && typeof video.audioTracks.length === 'number') {
        if (video.readyState >= HTMLMediaElement.HAVE_METADATA) {
            return { hasAudio: video.audioTracks.length > 0, confident: true };
        }
        return { hasAudio: true, confident: false };
    }

    if (typeof video.webkitAudioDecodedByteCount === 'number') {
        if (video.webkitAudioDecodedByteCount > 0) {
            return { hasAudio: true, confident: true };
        }
        if (video.currentTime > AUDIO_DETECT_PLAYBACK_SECONDS) {
            return { hasAudio: false, confident: true };
        }
        return { hasAudio: true, confident: false };
    }

    return { hasAudio: true, confident: false };
}

/**
 * Show or hide the mute control based on whether the active video has audio.
 * Keeps the button visible until detection is confident that there is no audio.
 *
 * @param {Element} videoComponent
 */
function updateMuteButtonVisibility(videoComponent) {
    if (!videoComponent) return;

    const muteButton = queryFirst('.mute-video', videoComponent);
    if (!muteButton) return;

    const activeVideo = getActiveVideoEl(videoComponent);
    const { hasAudio, confident } = detectVideoAudio(activeVideo);

    if (confident && !hasAudio) {
        addClass(muteButton, HIDDEN_CLASS);
    } else {
        removeClass(muteButton, HIDDEN_CLASS);
    }
}

/**
 * @param {Element} videoComponent
 */
function setUserPaused(videoComponent) {
    videoComponent?.setAttribute(USER_PAUSED_ATTR, 'true');
}

/**
 * @param {Element} videoComponent
 */
function clearUserPaused(videoComponent) {
    videoComponent?.removeAttribute(USER_PAUSED_ATTR);
}

/**
 * @param {Element} videoComponent
 * @returns {boolean}
 */
function isUserPaused(videoComponent) {
    return Boolean(videoComponent?.hasAttribute(USER_PAUSED_ATTR));
}

/**
 * Play a video after ensuring lazy sources are loaded. Swallows autoplay policy rejections.
 *
 * @param {HTMLVideoElement} videoEl
 * @returns {Promise<boolean>}
 */
async function playVideo(videoEl) {
    if (!videoEl) return false;

    await loadLazyVideo(videoEl);

    try {
        await videoEl.play();
        return true;
    } catch {
        return false;
    }
}

/**
 * @param {Element} videoComponent
 * @returns {HTMLVideoElement|null}
 */
function getActiveVideoEl(videoComponent) {
    if (!videoComponent) return null;

    const mainVideo = queryFirst(`.${mainVideoClass}`, videoComponent);
    const mobileVideo = queryFirst(`.${mobileVideoClass}`, videoComponent);

    if (videoComponent.closest('.hero-asset') || videoComponent.closest('.pd-banner')) {
        const isLandscape = window.innerWidth > window.innerHeight;
        return isLandscape ? mainVideo : mobileVideo || mainVideo;
    }

    return matchesBreakpoint('lg') ? mainVideo : mobileVideo || mainVideo;
}

/**
 * Sync play/mute control UI with the active video state.
 *
 * @param {Element} videoComponent
 * @param {boolean} isPlaying
 */
function updateVideoControlState(videoComponent, isPlaying) {
    if (!videoComponent) return;

    const playButton = queryFirst('.play-video', videoComponent);
    const muteButton = queryFirst('.mute-video', videoComponent);
    const activeVideo = getActiveVideoEl(videoComponent);

    if (playButton) {
        if (isPlaying) {
            addClass(playButton, PLAYED_CLASS);
        } else {
            removeClass(playButton, PLAYED_CLASS);
        }

        const playLabel = playButton.dataset.playLabel || 'Play';
        const pauseLabel = playButton.dataset.pauseLabel || 'Pause video';
        playButton.setAttribute('aria-label', isPlaying ? pauseLabel : playLabel);
    }

    if (muteButton && activeVideo) {
        if (activeVideo.muted) {
            addClass(muteButton, MUTED_CLASS);
            removeClass(muteButton, UNMUTED_CLASS);
        } else {
            addClass(muteButton, UNMUTED_CLASS);
            removeClass(muteButton, MUTED_CLASS);
        }
    }

    updateMuteButtonVisibility(videoComponent);
}

/**
 * @param {Element} videoComponent represents a video component
 * @returns {Element} existing video mainVideo or mobileVideo
 */
function hasVideos(videoComponent) {
    if (!videoComponent) return null;

    const mainVideo = queryFirst(`.${mainVideoClass}`, videoComponent);
    const mobileVideo = queryFirst(`.${mobileVideoClass}`, videoComponent);
    return mainVideo || mobileVideo;
}

/**
 * @param {Element} videoComponent represents a video component
 */
function initVideos(videoComponent) {
    if (!videoComponent || hasClass(videoComponent, 'initialized')) return;

    addClass(videoComponent, 'initialized');
    const mainVideo = queryFirst(`.${mainVideoClass}`, videoComponent);
    const mobileVideo = queryFirst(`.${mobileVideoClass}`, videoComponent);
    const playButton = queryFirst('.play-video', videoComponent);
    const muteButton = queryFirst('.mute-video', videoComponent);
    const captionVideoElement = queryFirst('.caption-video', videoComponent);
    const allVideos = [mainVideo, mobileVideo].filter(Boolean);

    let touchStart;
    let touchEnd;
    const scrollThreshold = 2;

    /**
     * Changes the state of video and the play button depending on whether or not the video is play/pause.
     * @param {HTMLElement} video Element representing  a video component
     */
    async function togglePlay(video) {
        if (!video) return;

        if (video.paused) {
            clearUserPaused(videoComponent);
            const playing = await playVideo(video);
            updateVideoControlState(videoComponent, playing);
        } else {
            video.pause();
            updateVideoControlState(videoComponent, false);
            setUserPaused(videoComponent);
        }
    }

    /**
     * Changes the state of the mute button depending on whether or not the video is off/on.
     * @param {HTMLElement} video Element representing  a video component
     */
    function toggleMute(video) {
        video.muted = !video.muted;
        if (video.muted) {
            removeClass(muteButton, UNMUTED_CLASS);
            addClass(muteButton, MUTED_CLASS);
        } else {
            removeClass(muteButton, MUTED_CLASS);
            addClass(muteButton, UNMUTED_CLASS);
        }
    }

    /**
     * Shows or hides the caption button depending on whether or not a video caption is off/on.
     * @param {Array} allVideos Array containing all video elements within a video component (mobile and desktop)
     * @param {HTMLElement} captionButton Element representing the caption button icon for a video component
     */
    function toggleCaption(allVideos, captionButton) {
        const captionOnIcon = queryFirst('.svg-video-caption-on', captionButton);
        const captionOffIcon = queryFirst('.svg-video-caption-off', captionButton);

        // Find the currently visible video (either mobile or desktop)
        const visibleVideo = allVideos.find(video => video.offsetParent !== null);

        if (visibleVideo?.textTracks?.length) {
            const track = visibleVideo.textTracks[0];

            // Check if cues are not null
            if (track.cues && track.cues.length > 0) {
                const videoCaptionIsOn = track.mode === 'showing';

                if (videoCaptionIsOn) {
                    track.mode = 'hidden';
                    addClass(captionOnIcon, HIDDEN_CLASS);
                    removeClass(captionOffIcon, HIDDEN_CLASS);
                } else {
                    track.mode = 'showing';
                    removeClass(captionOnIcon, HIDDEN_CLASS);
                    addClass(captionOffIcon, HIDDEN_CLASS);
                }
            } else {
                // If cues are null or empty, hide the caption button
                addClass(captionButton, HIDDEN_CLASS);
            }
        } else {
            // Hide caption button if no text tracks
            addClass(captionButton, HIDDEN_CLASS);
        }
    }

    playButton?.addEventListener('click', function () {
        togglePlay(getActiveVideoEl(videoComponent));
    });

    muteButton?.addEventListener('click', function () {
        toggleMute(getActiveVideoEl(videoComponent));
    });

    if (captionVideoElement) {
        captionVideoElement.addEventListener('click', () => {
            const visibleVideo = allVideos.find(video => video.offsetParent !== null);
            if (visibleVideo?.textTracks?.length) {
                const track = visibleVideo.textTracks[0];
                track.mode = track.mode === 'hidden' ? 'showing' : 'hidden';
                toggleCaption([visibleVideo], captionVideoElement);
            }
        });

        toggleCaption(allVideos, captionVideoElement);
    }

    /**
     * @param {Element} video touch event handling
     */
    function handleVideoTouch(video) {
        video.addEventListener('touchstart', e => {
            touchStart = e.touches[0].clientY;
        });

        video.addEventListener('touchend', e => {
            touchEnd = e.changedTouches[0].clientY;

            if (Math.abs(touchEnd - touchStart) < scrollThreshold) {
                togglePlay(video);
            }
        });
    }

    /**
     * Re-check mute visibility as metadata loads and Chromium decodes audio.
     * @param {HTMLVideoElement} video
     */
    function bindAudioDetection(video) {
        const onDetect = () => {
            updateMuteButtonVisibility(videoComponent);
            const { confident } = detectVideoAudio(video);
            if (confident) {
                video.removeEventListener('loadedmetadata', onDetect);
                video.removeEventListener('loadeddata', onDetect);
                video.removeEventListener('timeupdate', onDetect);
                video.removeEventListener('playing', onDetect);
            }
        };

        video.addEventListener('loadedmetadata', onDetect);
        video.addEventListener('loadeddata', onDetect);
        video.addEventListener('timeupdate', onDetect);
        video.addEventListener('playing', onDetect);
        onDetect();
    }

    [mainVideo, mobileVideo].forEach(video => {
        if (video) {
            handleVideoTouch(video);
            bindAudioDetection(video);

            video.addEventListener('play', () => {
                updateVideoControlState(videoComponent, true);
            });

            video.addEventListener('pause', () => {
                updateVideoControlState(videoComponent, false);
            });

            video.addEventListener('ended', () => {
                updateVideoControlState(videoComponent, false);
            });
        }
    });

    const activeVideo = getActiveVideoEl(videoComponent);

    updateVideoControlState(videoComponent, activeVideo && !activeVideo.paused);
    updateMuteButtonVisibility(videoComponent);

    /**
     * Sync mute from the shared control, then transfer play state across desktop/mobile
     * variants (orientation or breakpoint swap). Never pause the active video on cold start —
     * that races with lazy-load + HTML autoplay.
     */
    async function syncVideoState() {
        const nextActiveVideo = getActiveVideoEl(videoComponent);
        const inactiveVideo = nextActiveVideo === mainVideo ? mobileVideo : mainVideo;

        if (inactiveVideo) {
            const wasPlaying = !inactiveVideo.paused;
            inactiveVideo.pause();
            inactiveVideo.muted = true;

            const shouldPlay =
                !isUserPaused(videoComponent) &&
                (wasPlaying || Boolean(nextActiveVideo?.hasAttribute('autoplay')));

            if (nextActiveVideo && shouldPlay) {
                if (muteButton) {
                    nextActiveVideo.muted = muteButton.classList.contains(MUTED_CLASS);
                }
                const playing = await playVideo(nextActiveVideo);
                updateVideoControlState(videoComponent, playing);
                return;
            }
        }

        if (nextActiveVideo && muteButton) {
            nextActiveVideo.muted = muteButton.classList.contains(MUTED_CLASS);
        }

        updateMuteButtonVisibility(videoComponent);
    }

    let currentOrientation = window.innerWidth > window.innerHeight ? 'landscape' : 'portrait';

    if (videoComponent._onResize) {
        window.removeEventListener('resize', videoComponent._onResize);
    }

    videoComponent._onResize = () => {
        let newOrientation = window.innerWidth > window.innerHeight ? 'landscape' : 'portrait';

        if (newOrientation !== currentOrientation) {
            currentOrientation = newOrientation;
            syncVideoState();
        }
    };

    window.addEventListener('resize', videoComponent._onResize);
}

/**
 * Product highlight video: overlay uses an anchor (not a button).
 * Click is delegated to the real Shop the look control (.js-highlight-shop-the-look, same data-pid).
 */
function initHighlightShopVideoProxy() {
    if (highlightShopProxyInitialized || !document.body) return;

    highlightShopProxyInitialized = true;
    document.body.addEventListener('click', e => {
        const proxy = e.target?.closest('.js-highlight-shop-proxy');
        if (!proxy?.closest('.video-component')) return;

        e.preventDefault();

        const { proxyPid } = proxy.dataset;

        if (!proxyPid) return;

        const asset = proxy.closest('.product-highlight-asset');
        if (!asset) return;

        const delegate = queryFirst(`.js-highlight-shop-the-look[data-pid="${proxyPid}"]`, asset);

        delegate?.click();
    });
}

/**
 * Page-level init for processInclude (main.js).
 * Pass the default export (a function) so only this runs — not helpers hung on it.
 */
function init() {
    queryAll('.video-component').forEach(videoComponent => {
        if (hasVideos(videoComponent)) {
            initVideos(videoComponent);
        }
    });
    initHighlightShopVideoProxy();
}

// Attached for CommonJS callers (e.g. product/detail.js); not invoked by processInclude
init.initVideos = initVideos;
init.hasVideos = hasVideos;
init.getActiveVideoEl = getActiveVideoEl;
init.updateVideoControlState = updateVideoControlState;
init.updateMuteButtonVisibility = updateMuteButtonVisibility;
init.detectVideoAudio = detectVideoAudio;

export default init;
